import { Nullable } from 'constants/types'
import { AriaAttr, EventType } from 'constants/enums'
import { isMobile } from 'utils/system/device'
import { VideoPlayerStore } from './player/store'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { StickyWrapperEvent } from 'constants/enums'
import { VideoProviderType } from 'components/video/types'
import { ATTR_NAME } from 'constants/attr-name'

const FEATURED_VIDEO_FIXED_CLASS = 'featured-video--fixed'
const FEATURED_VIDEO_MINIMIZED_CLASS = 'player-wrapper--minimized'
const FEATURED_VIDEO_FLOATING_CLASS = 'player-wrapper--float'
const FEATURED_VIDEO_MINIMIZED_TRIGGER_FACTOR = 0.2
const FEATURED_VIDEO_BOTTOM_POSITION = 24 // default spacing -> 1.5rem
const ARTICLE_LIMIT_ALERT_HEIGHT = 80 // Piano injected iframe's height -> 5.5rem / 80px
const Y_SCROLLING_INTERVAL = 500 // saves scrolling position every 500 ms
const DEBOUNCE_TIME = 20
const MOBILE_LANDSCAPE_MAX_WIDTH = 767 // max-width for mobile devices in landscape orientation
const ORIENTATION_CHANGE_DELAY = 100 // delay after orientation change to allow browser to complete rotation
const RESIZE_DEBOUNCE_TIME = 250 // debounce time for resize event listener

export class Sticky extends ComponentBase {
    public closeVid = false
    public trigger = 0
    public lastScrollTop = 0
    public scrollyAxisPosition = 0
    public scrollUp: boolean
    public featuredVideoBottomPosition: number
    public articleLimitAlert = false
    public footerVisible = false
    public intervalId = 0
    public observer: IntersectionObserver
    private stickyActivated = false // Track if sticky has been activated
    private triggerStateGetter?: () => boolean // Function to check if trigger point reached
    private abTest: Partial<{
        useNewStoryPageTemplate: boolean
        useSTNFloatingDCSImplementation?: boolean
    }> = {}
    private provider: VideoProviderType | ''
    private closeFeaturedVid: Nullable<HTMLElement> =
        this.getElement('#close-video')
    private toggleFeaturedVid: Nullable<HTMLElement> =
        this.getElement('#toggle-video')
    private toggleVideoContainer: Nullable<HTMLElement> =
        this.getElement('#video-container')
    private header: Nullable<HTMLElement> =
        this.getElement<HTMLElement>('#header')
    private footer: Nullable<HTMLElement> =
        this.getElement<HTMLElement>('#footer')
    private playerEl: Nullable<HTMLElement> =
        this.getElement<HTMLElement>('#player')
    private videoTitle: Nullable<HTMLSpanElement> =
        this.getElement<HTMLSpanElement>(`[${ATTR_NAME.video.sticky.title}]`)

    get stickyWrapper(): Nullable<HTMLElement> {
        return this.getElement('.featured-video--fixed .player-wrapper')
    }

    get bottomStickyAdHeight(): number {
        return (
            this.getElement<HTMLElement>('.fixed-bottom-ad-container')
                ?.offsetHeight ?? 0
        )
    }

    get articleCounterHeight(): number {
        return (
            this.getElement<HTMLElement>('#article-counter')?.offsetHeight ?? 0
        )
    }

    private get isVideoMinimized(): boolean {
        if (!this.toggleVideoContainer) {
            return false
        }
        return this.toggleVideoContainer.classList.contains(
            FEATURED_VIDEO_MINIMIZED_CLASS
        )
    }

    public constructor(
        public featuredVid: HTMLElement,
        triggerStateGetter?: () => boolean
    ) {
        super()
        this.triggerStateGetter = triggerStateGetter ?? undefined
        this.provider =
            (featuredVid.dataset['provider'] as VideoProviderType) || ''
        const pageData = window.document.getElementById('page-data')
        if (pageData) {
            this.abTest = JSON.parse(pageData.innerHTML).page.abTest
        }
        // 1. Start tracking footer position
        this.observer = new IntersectionObserver(
            this.setFooterVisible.bind(this)
        )
        // 2. Set sticky trigger:  how much of a video elements in pixels has to be hidden
        // initial value request 20% or factor of 0.2
        // Use toggleVideoContainer if available, otherwise use featuredVid itself (for video-playlist)
        const triggerElement = this.toggleVideoContainer || this.featuredVid
        if (triggerElement) {
            const height = triggerElement.getBoundingClientRect().height
            this.trigger = height * FEATURED_VIDEO_MINIMIZED_TRIGGER_FACTOR
        }

        this.dockStickyToFooter()

        // 4. Listen to tinypass injection of article limit alert
        window.addEventListener('articleLimitAlertEvent', ((
            event: CustomEvent
        ) => {
            this.articleLimitAlert = event['detail']['limit'] ? true : false
            // If page scrolled to sticky video
            // when article limit alert injected
            // reposition sticky video
            this.dockStickyToFooter()
        }) as EventListener)

        // 5. Listen to close sticky events
        if (this.closeFeaturedVid) {
            this.closeFeaturedVid.addEventListener(EventType.CLICK, () =>
                this.closeStickyVideo()
            )
        }

        // 6. Listen to minimize/maximize sticky events
        if (this.toggleFeaturedVid) {
            this.toggleFeaturedVid.addEventListener(EventType.CLICK, () =>
                this.toggleStickyVideo()
            )
        }

        // 8. Position sticky video and
        // reposition on scroll
        if (this.applyStickyBehaviour()) {
            this.stickyVideo()
        }

        // 9. Listen to sticky ad close event
        window.addEventListener('stickyAdClosedEvent', ((
            event: CustomEvent
        ) => {
            this.handleStickyAdCloseEvent(event.detail.position === 'bottom')
        }) as EventListener)

        // 10. Video Title Change
        document.addEventListener(
            StickyWrapperEvent.CHANGE_TITLE,
            (event: CustomEvent) => {
                if (this.videoTitle) {
                    this.videoTitle.innerHTML = event.detail.title
                }
            }
        )

        // Initialize the debounced function once and store it.
        const debounced = this.debouncedStickyVideo()
        window.addEventListener(EventType.SCROLL, debounced)

        // 11. Handle orientation changes to re-evaluate sticky behavior
        // This ensures sticky video is removed/prevented when rotating to landscape on mobile
        window.addEventListener('orientationchange', () => {
            // Re-evaluate after orientation change completes
            setTimeout(() => {
                this.stickyVideo()
            }, ORIENTATION_CHANGE_DELAY)
        })

        // 12. Handle resize as backup for browsers that don't fire orientationchange
        // Only re-evaluate if video is currently sticky to avoid unnecessary checks
        const debouncedResize = this.debounce(() => {
            if (
                this.featuredVid.classList.contains(FEATURED_VIDEO_FIXED_CLASS)
            ) {
                this.stickyVideo()
            }
        }, RESIZE_DEBOUNCE_TIME)
        window.addEventListener(EventType.RESIZE, debouncedResize)

        /**
         * for BC-229 : fix unwanted autoscroll to top after user exits full screen mode of video player in iPad iOS v12
         */
        if (window.navigator.userAgent.includes('iPad; CPU OS 12_')) {
            this.setTimer()
            window.addEventListener(EventType.RESIZE, () =>
                this.noScrolltoTop()
            )
            if (document.body) {
                window.addEventListener(EventType.TOUCH_START, () => {
                    this.scrollyAxisPosition = window.scrollY
                    this.setTimer()
                })
                window.addEventListener(EventType.TOUCH_END, () => {
                    this.scrollyAxisPosition = window.scrollY
                    this.setTimer()
                })
            }
            window.addEventListener(EventType.LOAD, () => this.setTimer())
            window.addEventListener(EventType.BEFORE_UNLOAD, () =>
                this.clearTimer()
            )
        }
    }

    /**
     * for BC-229 : fix unwanted autoscroll to top after user exits full screen mode of video player
     */
    public noScrolltoTop(): void {
        this.clearTimer()
        window.scrollTo(0, this.scrollyAxisPosition)
    }

    public setTimer(): void {
        this.clearTimer()
        this.intervalId = window.setInterval(() => {
            this.scrollyAxisPosition = window.scrollY
        }, Y_SCROLLING_INTERVAL)
    }

    public clearTimer(): void {
        window.clearInterval(this.intervalId)
    }

    /**
     * Set the footerVisible flag
     */
    public setFooterVisible(entries: IntersectionObserverEntry[]): void {
        for (const entry of entries) {
            this.footerVisible = entry.isIntersecting
        }
    }

    /**
     * Set when sticky should be applied
     */
    public applyStickyBehaviour(): Nullable<boolean> {
        if (!this.header) {
            return false
        }

        // Disable sticky video on mobile landscape (max-width: 767px and landscape orientation)
        if (
            window.innerWidth <= MOBILE_LANDSCAPE_MAX_WIDTH &&
            window.matchMedia('(orientation: landscape)').matches
        ) {
            return false
        }

        // If trigger state getter is provided, only apply sticky after trigger point is reached
        // This allows sticky to be delayed until a specific element (e.g., #keyTakeaways) is visible
        if (this.triggerStateGetter && !this.triggerStateGetter()) {
            return false
        }

        const store = VideoPlayerStore.getInstance()
        if (
            // This setting will be removed with next release https://postmediadigital.atlassian.net/browse/CHEET-7824
            !this.abTest.useSTNFloatingDCSImplementation &&
            this.provider === VideoProviderType.SendToNews
        ) {
            // STN handles float on its own
            return false
        }

        let state = ''

        try {
            state = store.featurePlayer?.getState() ?? ''
            // eslint-disable-next-line no-empty
        } catch (e) {}
        if (
            this.abTest.useNewStoryPageTemplate &&
            state &&
            state !== 'playing'
        ) {
            return false
        }

        const rect = this.featuredVid.getBoundingClientRect()
        const top = rect.top
        const bottom = rect.bottom
        const headerHeight = this.header.offsetHeight
        const windowHeight = window.innerHeight
        const triggerThreshold = this.trigger * -1

        // Video should stick when it's no longer fully visible in the viewport:
        // 1. Top scrolled past header: top - headerHeight < triggerThreshold
        // 2. Bottom scrolled past top of screen: bottom < headerHeight (video scrolled up beyond view)
        // 3. Top scrolled past bottom of screen: top > windowHeight (video scrolled down beyond view)
        const scrolledPastTop = top - headerHeight < triggerThreshold
        const scrolledAboveScreen = bottom < headerHeight
        const scrolledBelowScreen = top > windowHeight
        const shouldStick =
            scrolledPastTop || scrolledAboveScreen || scrolledBelowScreen

        // Video is back in viewport when it's visible between header and bottom of screen
        const videoInViewport =
            top >= headerHeight - this.trigger &&
            bottom > headerHeight &&
            top < windowHeight

        // If user scrolled back to original video position, deactivate sticky
        if (this.stickyActivated && videoInViewport) {
            this.stickyActivated = false
            return false
        }

        // Activate sticky when video scrolls out of view (up or down)
        if (shouldStick) {
            this.stickyActivated = true
        }
        return this.stickyActivated
    }

    /**
     * Reposition sticky video on ad close event
     * when ad is bottom
     */
    public handleStickyAdCloseEvent(bottom: boolean): void {
        if (bottom) {
            // reposition sticky video
            this.dockStickyToFooter()
        }
    }

    /**
     * Add sticky video css class
     * reset  video behaviour on non-sticky removal
     */
    public stickyVideo(): void {
        if (this.header) {
            let stickyVisible
            const wasSticky = this.featuredVid.classList.contains(
                FEATURED_VIDEO_FIXED_CLASS
            )
            if (this.applyStickyBehaviour() && !this.closeVid) {
                this.featuredVid.classList.add(FEATURED_VIDEO_FIXED_CLASS)
                stickyVisible = 'false'
                // Ensure player-wrapper is keyboard focusable when sticky
                if (this.toggleVideoContainer && !wasSticky) {
                    this.toggleVideoContainer.setAttribute('tabindex', '0')
                }
            } else {
                this.featuredVid.classList.remove(FEATURED_VIDEO_FIXED_CLASS)
                // Reset to initial aria-label
                if (this.isVideoMinimized && this.toggleFeaturedVid) {
                    this.toggleFeaturedVid.setAttribute(
                        AriaAttr.LABEL,
                        'Minimize video'
                    )
                }
                if (this.toggleVideoContainer) {
                    this.toggleVideoContainer.classList.remove(
                        FEATURED_VIDEO_MINIMIZED_CLASS
                    )
                    // Remove tabindex when not sticky
                    this.toggleVideoContainer.removeAttribute('tabindex')
                }
                stickyVisible = 'true'
            }
            if (this.closeFeaturedVid) {
                this.closeFeaturedVid.setAttribute(
                    AriaAttr.HIDDEN,
                    stickyVisible
                )
            }
            if (this.toggleFeaturedVid) {
                this.toggleFeaturedVid.setAttribute(
                    AriaAttr.HIDDEN,
                    stickyVisible
                )
            }
        }
        // Sticky should not cover footer
        this.dockStickyToFooter()
    }

    /**
     * Determine scroll direction
     * to use for docking sticky logic on mobile
     */
    public getScrollDirection(): void {
        // Get the number of pixels the document is currently scrolled along the vertical axis
        const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop

        // Set direction key value
        this.scrollUp = scrollTop > this.lastScrollTop ? false : true
        // Reset negative scroll for last value
        this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop
    }

    /**
     * Adjust sticky video position
     * above visible footer
     */
    public dockStickyToFooter(): void {
        if (!this.footer || !this.stickyWrapper) {
            return
        }

        let dynamicStickyPosition
        // Init bottom position
        this.featuredVideoBottomPosition = this.articleLimitAlert
            ? ARTICLE_LIMIT_ALERT_HEIGHT + FEATURED_VIDEO_BOTTOM_POSITION
            : FEATURED_VIDEO_BOTTOM_POSITION
        // Get root element's height (<html>)
        const windowHeight: number = document.documentElement.clientHeight
        // Get footer's size and its position relative to the viewport
        const footerCoords = this.footer.getBoundingClientRect()

        this.observer.observe(this.footer)
        // Calculate when footer is visible
        if (this.articleLimitAlert) {
            dynamicStickyPosition = Math.round(
                windowHeight -
                    this.featuredVideoBottomPosition -
                    footerCoords.top +
                    FEATURED_VIDEO_BOTTOM_POSITION
            )
        } else {
            dynamicStickyPosition = Math.round(windowHeight - footerCoords.top)
        }

        // Calculate bottom distance for sticky video
        let spacer = this.footerVisible
            ? dynamicStickyPosition + this.featuredVideoBottomPosition
            : this.featuredVideoBottomPosition
        spacer = spacer + this.bottomStickyAdHeight + this.articleCounterHeight

        // Set bottom distance for sticky video
        this.stickyWrapper.style.bottom = `${spacer}px`

        // MOBILE logic
        if (isMobile()) {
            // Track scroll direction
            this.getScrollDirection()
            // Get sticky video wrapper's size and its position relative to the viewport
            const stickyCoords = this.stickyWrapper.getBoundingClientRect()
            const stickyFullyVisible = stickyCoords.top > 0
            if (this.footerVisible) {
                this.stickyWrapper.classList.add(FEATURED_VIDEO_FLOATING_CLASS)
            } else if (stickyFullyVisible && this.scrollUp) {
                this.stickyWrapper.classList.remove(
                    FEATURED_VIDEO_FLOATING_CLASS
                )
            }
        }
    }

    /**
     * Pause sticky video
     */
    private pauseStickyVideo(): void {
        const store = VideoPlayerStore.getInstance()
        // Attempt to pause programmatic players only (YouTube and JW)
        // Distroscale and Send to News cannot be paused
        if (store.featurePlayer && !!store.featurePlayer.isPausable()) {
            store.featurePlayer.pause()
        }
    }

    /**
     * Remove sticky video CSS class
     */
    public closeStickyVideo(): void {
        this.featuredVid.classList.remove(FEATURED_VIDEO_FIXED_CLASS)
        this.stickyActivated = false // Reset sticky activation state
        // Wrap the operations that don't require immediate execution in setTimeout
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent(StickyWrapperEvent.CLOSE))
            const store = VideoPlayerStore.getInstance()
            if (store.featurePlayer && !store.featurePlayer.isAdPlaying) {
                this.pauseStickyVideo()
            }

            if (store.featurePlayer) {
                store.featurePlayer.dispatchAnalyticsStickyClose()
            }

            this.closeVid = true

            if (this.closeFeaturedVid) {
                this.closeFeaturedVid.setAttribute(AriaAttr.HIDDEN, 'true')
            }
            if (this.toggleFeaturedVid) {
                this.toggleFeaturedVid.setAttribute(AriaAttr.HIDDEN, 'true')
            }
        }, 0) // Timeout of 0ms to defer until the next event loop
    }

    /**
     * Minimize/maximize fixed sticky video
     */
    public toggleStickyVideo(): void {
        if (!this.toggleVideoContainer) {
            return
        }

        let hiddenState, toggleBtnAction
        const featured = VideoPlayerStore.getInstance().featurePlayer
        if (this.isVideoMinimized) {
            // Maximize video
            this.toggleVideoContainer.classList.remove(
                FEATURED_VIDEO_MINIMIZED_CLASS
            )
            // Prepare aria-hidden tag attribute
            hiddenState = 'false'
            toggleBtnAction = 'Minimize video'
            if (featured) {
                featured.dispatchAnalyticsStickyRestore()
            }
        } else {
            window.dispatchEvent(new CustomEvent(StickyWrapperEvent.MINIMIZE))
            // Minimize video
            this.toggleVideoContainer.classList.add(
                FEATURED_VIDEO_MINIMIZED_CLASS
            )
            // Prepare aria-hidden tag attribute
            hiddenState = 'true'
            toggleBtnAction = 'Restore video'
            this.pauseStickyVideo()
            if (featured) {
                featured.dispatchAnalyticsStickyMinimize()
            }
        }
        // Set ARIA attributes
        if (this.playerEl) {
            this.playerEl.setAttribute(AriaAttr.HIDDEN, hiddenState)
        }
        if (this.toggleFeaturedVid) {
            this.toggleFeaturedVid.setAttribute(AriaAttr.LABEL, toggleBtnAction)
        }
        // Minimized sticky on mobile - close button hidden
        if (isMobile() && this.closeFeaturedVid) {
            this.closeFeaturedVid.setAttribute(AriaAttr.HIDDEN, hiddenState)
        }
    }

    /**
     * Returns a debounced version of the stickyVideo method to ensure that it is
     * called at most once in a specified time period. This helps in optimizing
     * performance by limiting the frequency of its execution, especially useful
     * for events that fire rapidly, like window scrolling.
     */
    private debouncedStickyVideo() {
        return this.debounce(this.stickyVideo.bind(this), DEBOUNCE_TIME)
    }

    /**
     * Creates a debounced function that will invoke the provided callback only
     * after the specified waitFor period has elapsed since the last time it was
     * invoked. This limits the rate at which the callback is executed, enhancing
     * performance and reducing the load on the main thread.
     *
     * @param callback The function to debounce.
     * @param waitFor The period to wait before invoking the callback after the last call.
     * @returns A debounced version of the provided callback function.
     */
    private debounce<T extends (...args: any[]) => void>(
        callback: T,
        waitFor: number
    ) {
        let timeout: ReturnType<typeof setTimeout> | undefined = undefined
        return (...args: Parameters<T>): void => {
            const next = () => callback(...args)
            if (timeout !== undefined) {
                clearTimeout(timeout)
            }
            timeout = setTimeout(next, waitFor)
        }
    }
}
